import React, { useEffect, useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Sparkles, TrendingUp, AlertCircle, HelpCircle, RefreshCw, Loader2 } from 'lucide-react';
import { MindMapNode, Language } from '../types';
import { parseMarkdown } from '../utils/markdownUtils';
import { getFollowUpAnalysis } from '../services/geminiService';

interface DetailedFeedbackModalProps {
    node: MindMapNode;
    onClose: () => void;
    onReanalyze: (nodeId: string, mode: 'detailed') => void;
    language: Language;
}

const LABELS = {
    english: {
        title: "Detailed Analysis",
        score: "Score",
        summary: "Summary",
        analysis: "Detailed Feedback",
        bonus: "Bonus Points",
        close: "Close",
        learnMore: "🔍 Learn More",
        reanalyze: "Re-analyze",
        analyzeWeak: "📊 Analyze Weaknesses",
        detailStrengths: "✨ Detail Strengths",
        suggestImprovements: "💡 Suggest Improvements",
        explainBonus: "📚 Explain Bonus",
        followUp: "Follow-up Analysis",
        waitCooldown: "Please wait before next action"
    },
    vietnamese: {
        title: "Phân tích chi tiết",
        score: "Điểm",
        summary: "Tóm tắt",
        analysis: "Phân tích chi tiết",
        bonus: "Điểm thưởng",
        close: "Đóng",
        learnMore: "🔍 Tìm hiểu thêm",
        reanalyze: "Phân tích lại",
        analyzeWeak: "📊 Phân tích điểm chưa đúng",
        detailStrengths: "✨ Chi tiết điểm tốt",
        suggestImprovements: "💡 Gợi ý cải thiện",
        explainBonus: "📚 Giải thích bonus",
        followUp: "Phân tích thêm",
        waitCooldown: "Vui lòng đợi trước khi thao tác tiếp"
    }
};

export const DetailedFeedbackModal: React.FC<DetailedFeedbackModalProps> = ({ node, onClose, onReanalyze, language }) => {
    const labels = LABELS[language];

    // State management
    const [followUpResponse, setFollowUpResponse] = useState<string | null>(null);
    const [isLoadingFollowUp, setIsLoadingFollowUp] = useState(false);
    const [isReanalyzing, setIsReanalyzing] = useState(false);
    const [lastActionTime, setLastActionTime] = useState(0);
    const [detailedExpanded, setDetailedExpanded] = useState(false);

    const handleClose = useCallback(() => {
        onClose();
    }, [onClose]);

    // Quick Action Handler with Rate Limiting
    const handleQuickAction = async (action: string) => {
        console.log('🔵 Quick Action clicked:', action);
        const now = Date.now();
        const cooldown = 30000; // 30 seconds

        if (now - lastActionTime < cooldown) {
            alert(labels.waitCooldown);
            return;
        }

        setLastActionTime(now);
        setIsLoadingFollowUp(true);
        setFollowUpResponse(null);
        console.log('📤 Calling getFollowUpAnalysis...');

        try {
            const response = await getFollowUpAnalysis(
                action,
                node.content,
                node.analysis?.detailedFeedback || '',
                node.analysis?.bonusPoints,
                language
            );
            setFollowUpResponse(response);
        } catch (error) {
            console.error('Follow-up analysis failed:', error);
            setFollowUpResponse(language === 'vietnamese'
                ? '❌ Có lỗi xảy ra. Vui lòng thử lại.'
                : '❌ An error occurred. Please try again.');
        } finally {
            setIsLoadingFollowUp(false);
        }
    };

    // Re-analyze Handler with Confirmation
    const handleReanalyze = () => {
        const confirmed = window.confirm(
            language === 'vietnamese'
                ? 'Phân tích lại sẽ ghi đè kết quả cũ. Tiếp tục?'
                : 'Re-analyzing will overwrite existing results. Continue?'
        );

        if (confirmed) {
            setIsReanalyzing(true);
            onReanalyze(node.id, 'detailed');
            // Close modal after triggering reanalysis
            setTimeout(() => {
                handleClose();
            }, 500);
        }
    };

    // Lock body scroll when modal is open
    useEffect(() => {
        const originalOverflow = document.body.style.overflow;
        const originalPaddingRight = document.body.style.paddingRight;

        // Calculate scrollbar width and lock scroll
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
        document.body.style.overflow = 'hidden';
        document.body.style.paddingRight = `${scrollbarWidth}px`;

        return () => {
            document.body.style.overflow = originalOverflow;
            document.body.style.paddingRight = originalPaddingRight;
        };
    }, []);

    // Close on ESC key
    useEffect(() => {
        const handleEsc = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                handleClose();
            }
        };
        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, [handleClose]);

    if (!node.analysis) return null;

    const { score, feedback, detailedFeedback, bonusPoints } = node.analysis;

    // Determine color based on score
    let scoreColorClass = "text-gray-600";
    let bgColorClass = "bg-gray-50";
    let borderColorClass = "border-gray-200";

    if (score >= 80) {
        scoreColorClass = "text-green-600 dark:text-green-400";
        bgColorClass = "bg-green-50 dark:bg-green-900/20";
        borderColorClass = "border-green-200 dark:border-green-900/30";
    } else if (score >= 50) {
        scoreColorClass = "text-yellow-600 dark:text-yellow-400";
        bgColorClass = "bg-yellow-50 dark:bg-yellow-900/20";
        borderColorClass = "border-yellow-200 dark:border-yellow-900/30";
    } else {
        scoreColorClass = "text-red-600 dark:text-red-400";
        bgColorClass = "bg-red-50 dark:bg-red-900/20";
        borderColorClass = "border-red-200 dark:border-red-900/30";
    }

    const modalContent = (
        <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-200"
            onClick={(e) => {
                // Only close if clicking directly on backdrop
                if (e.target === e.currentTarget) {
                    handleClose();
                }
            }}
        >
            <div
                className="relative w-full max-w-4xl max-h-[90vh] bg-white dark:bg-slate-800 rounded-2xl shadow-2xl flex flex-col animate-in zoom-in-95 duration-200"
                style={{ overscrollBehavior: 'contain' }}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b dark:border-slate-700 shrink-0">
                    <div className="flex items-center gap-3">
                        <div className={`w-12 h-12 rounded-xl ${bgColorClass} flex items-center justify-center`}>
                            <TrendingUp className={scoreColorClass} size={24} />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white">{labels.title}</h2>
                            <p className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-md">{node.title}</p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onMouseDown={(e) => {
                            e.stopPropagation();
                            handleClose();
                        }}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors z-50"
                    >
                        <X size={20} className="text-gray-500 dark:text-gray-400" />
                    </button>
                </div>

                {/* Content - with scroll isolation */}
                <div
                    className="flex-1 overflow-y-auto px-6 py-6 space-y-6"
                    style={{ overscrollBehavior: 'contain' }}
                    onWheel={(e) => e.stopPropagation()}
                    onTouchMove={(e) => e.stopPropagation()}
                >
                    {/* Score Section */}
                    <div className={`p-4 rounded-xl border ${borderColorClass} ${bgColorClass}`}>
                        <div className="flex items-center justify-between mb-3">
                            <span className="text-sm font-bold uppercase tracking-wider text-gray-600 dark:text-gray-400">
                                {labels.score}
                            </span>
                            <span className={`text-3xl font-black ${scoreColorClass}`}>{score}%</span>
                        </div>
                        <div className="h-2 w-full bg-gray-200 dark:bg-slate-700 rounded-full overflow-hidden">
                            <div
                                className={`h-full transition-all duration-1000 ${score >= 80 ? 'bg-green-500' : score >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                                    }`}
                                style={{ width: `${score}%` }}
                            />
                        </div>
                    </div>


                    {/* Summary Feedback */}
                    {feedback && (
                        <div className="space-y-2">
                            <div className="flex items-center gap-2">
                                <AlertCircle size={16} className="text-indigo-500" />
                                <h3 className="text-sm font-bold uppercase tracking-wider text-gray-600 dark:text-gray-400">
                                    {labels.summary}
                                </h3>
                            </div>
                            <div className={`p-4 rounded-lg border ${borderColorClass} ${bgColorClass}`}>
                                <p className="text-sm leading-relaxed text-gray-700 dark:text-gray-300">{feedback}</p>
                                
                                {/* AI Mode Indicator */}
                                {node.analysis?.strictnessUsed && (
                                    <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                            Đánh giá bởi chế độ: <span className="font-semibold">{
                                                node.analysis.strictnessUsed === 'strict' ? 'Rất Gắt 😤' :
                                                node.analysis.strictnessUsed === 'balanced' ? 'Cân Bằng ⚖️' :
                                                node.analysis.strictnessUsed === 'encouraging' ? 'Động Viên 🌟' :
                                                'Tùy Chỉnh ⚙️'
                                            }</span>
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Detailed Feedback */}
                    {detailedFeedback && (
                        <div className="space-y-2">
                            <div
                                className="flex items-center justify-between cursor-pointer select-none"
                                onClick={() => setDetailedExpanded(e => !e)}
                            >
                                <h3 className="text-sm font-bold uppercase tracking-wider text-gray-600 dark:text-gray-400">
                                    {labels.analysis}
                                </h3>
                                <span className="text-xs text-indigo-500 hover:text-indigo-700 font-medium">
                                    {detailedExpanded ? '▲ Thu gọn' : '▼ Xem thêm'}
                                </span>
                            </div>
                            <div className="p-4 rounded-lg bg-blue-50/50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-900/30">
                                <div
                                    className={`prose prose-sm dark:prose-invert max-w-none leading-relaxed overflow-hidden transition-all ${detailedExpanded ? '' : 'line-clamp-4'}`}
                                    dangerouslySetInnerHTML={{ __html: parseMarkdown(detailedFeedback) }}
                                />
                            </div>
                        </div>
                    )}

                    {/* Bonus Points */}
                    {bonusPoints && bonusPoints.length > 0 && (
                        <div className="space-y-2">
                            <div className="flex items-center gap-2">
                                <Sparkles size={16} className="text-emerald-500" />
                                <h3 className="text-sm font-bold uppercase tracking-wider text-gray-600 dark:text-gray-400">
                                    {labels.bonus}
                                </h3>
                            </div>
                            <div className="bg-emerald-50 dark:bg-emerald-900/10 border border-emerald-100 dark:border-emerald-900/30 rounded-lg p-4">
                                <ul className="space-y-2">
                                    {bonusPoints.map((point, index) => (
                                        <li key={index} className="flex items-start gap-2 text-sm text-emerald-800 dark:text-emerald-300">
                                            <span className="font-bold text-emerald-600 dark:text-emerald-400 mt-0.5">•</span>
                                            <span>{point}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    )}

                    {/* Quick Actions Section */}
                    {detailedFeedback && (
                        <div className="space-y-3">
                            <div className="flex items-center gap-2">
                                <HelpCircle size={16} className="text-indigo-500" />
                                <h3 className="text-sm font-bold uppercase tracking-wider text-gray-600 dark:text-gray-400">
                                    {labels.learnMore}
                                </h3>
                            </div>

                            <div className="grid grid-cols-2 gap-2">
                                <button
                                    onClick={() => handleQuickAction('analyze_weak')}
                                    onMouseDown={(e) => { e.stopPropagation(); handleQuickAction('analyze_weak'); }}
                                    disabled={isLoadingFollowUp}
                                    className="px-3 py-2 text-left text-sm font-medium bg-red-50 hover:bg-red-100 dark:bg-red-900/10 dark:hover:bg-red-900/20 text-red-700 dark:text-red-300 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed border border-red-100 dark:border-red-900/30"
                                >
                                    {labels.analyzeWeak}
                                </button>
                                <button
                                    onClick={() => handleQuickAction('detail_strengths')}
                                    onMouseDown={(e) => { e.stopPropagation(); handleQuickAction('detail_strengths'); }}
                                    disabled={isLoadingFollowUp}
                                    className="px-3 py-2 text-left text-sm font-medium bg-green-50 hover:bg-green-100 dark:bg-green-900/10 dark:hover:bg-green-900/20 text-green-700 dark:text-green-300 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed border border-green-100 dark:border-green-900/30"
                                >
                                    {labels.detailStrengths}
                                </button>
                                <button
                                    onClick={() => handleQuickAction('suggest_improvements')}
                                    onMouseDown={(e) => { e.stopPropagation(); handleQuickAction('suggest_improvements'); }}
                                    disabled={isLoadingFollowUp}
                                    className="col-span-2 px-3 py-2 text-left text-sm font-medium bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/10 dark:hover:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed border border-blue-100 dark:border-blue-900/30"
                                >
                                    {labels.suggestImprovements}
                                </button>
                            </div>

                            {/* Loading State */}
                            {isLoadingFollowUp && (
                                <div className="flex items-center justify-center gap-2 py-4">
                                    <Loader2 size={20} className="animate-spin text-indigo-500" />
                                    <span className="text-sm text-gray-500 dark:text-gray-400">Processing...</span>
                                </div>
                            )}

                            {/* Follow-up Response Display */}
                            {followUpResponse && (
                                <div className="space-y-2 animate-in fade-in slide-in-from-top-2 duration-300">
                                    <h3 className="text-sm font-bold uppercase tracking-wider text-gray-600 dark:text-gray-400">
                                        {labels.followUp}
                                    </h3>
                                    <div className="p-4 rounded-lg bg-purple-50/70 dark:bg-purple-900/10 border border-purple-100 dark:border-purple-900/30">
                                        <div
                                            className="prose prose-sm dark:prose-invert max-w-none leading-relaxed"
                                            dangerouslySetInnerHTML={{ __html: parseMarkdown(followUpResponse) }}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t dark:border-slate-700 flex justify-between shrink-0">
                    <button
                        type="button"
                        onClick={handleReanalyze}
                        onMouseDown={(e) => { e.stopPropagation(); handleReanalyze(); }}
                        disabled={isReanalyzing}
                        className="flex items-center gap-2 px-4 py-2 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <RefreshCw size={16} className={isReanalyzing ? 'animate-spin' : ''} />
                        {labels.reanalyze}
                    </button>

                    <button
                        type="button"
                        onMouseDown={(e) => {
                            e.stopPropagation();
                            handleClose();
                        }}
                        className="px-6 py-2 bg-indigo-600 hover:bg-indigo-500 text-white font-semibold rounded-lg transition-colors z-50"
                    >
                        {labels.close}
                    </button>
                </div>
            </div>
        </div>
    );

    // Use React Portal to render at document body level
    return createPortal(modalContent, document.body);
};
